import classNames from "classnames";
import { PieColorsMap, UserColorsMap } from "./constants";
import styles from "./styles.module.scss";
import { Bar, BarChart, Cell, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis } from "recharts";
import type { CSSProperties } from "react";

import { NativeSelect, Tooltip as ShadcnTooltip } from "@altimateai/lego";

interface AnalyticsTrendDataPoint {
  day: string | number;
  executions: number;
}

interface CustomTooltipProps {
  active?: boolean;
  payload?: Array<{ value: number }>;
  label?: string;
}

const CustomTooltip = ({ active, payload, label }: CustomTooltipProps) => {
  if (active && payload && payload.length) {
    return (
      <div className={styles.chart_tooltip}>
        <div className={styles.tooltip_header}>{label}</div>
        <div className={styles.tooltip_execs}>
          {payload[0].value === 0 ? "No Credits" : `${payload[0].value} credits`}
        </div>
      </div>
    );
  }

  return null;
};

interface TrendBarGraphProps {
  analyticsTrendData: AnalyticsTrendDataPoint[];
  period: string;
}

export const TrendBarGraph = ({ analyticsTrendData, period }: TrendBarGraphProps) => {
  const isWeek = period === "week";
  const customXTicks = analyticsTrendData
    .map(data => data.day)
    .filter((_, index) => index >= (isWeek ? 0 : 3));

  const dataWithZeroExecutions = analyticsTrendData.map(data => ({
    ...data,
    zeroExecutions: data.executions === 0 ? (isWeek ? 2 : 5) : 0,
  }));

  return (
    <div className={styles.trend_chart}>
      <ResponsiveContainer>
        <BarChart data={dataWithZeroExecutions}>
          <XAxis
            dataKey="day"
            interval={isWeek ? 0 : 7}
            ticks={customXTicks}
            label={{ value: "Day", position: "right", fill: "black" }}
            tickLine={false}
            axisLine={false}
            tick={{ fontSize: 8 }}
          />
          <Tooltip content={<CustomTooltip />} />
          <Bar
            barSize={isWeek ? 8 : 3}
            dataKey="executions"
            fill="#247EFE"
            stackId="executions"
            radius={[5, 5, 5, 5]}
          />
          <Bar
            barSize={isWeek ? 8 : 3}
            dataKey="zeroExecutions"
            fill="#D3E5FF"
            stackId="executions"
            radius={[5, 5, 5, 5]}
          />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

interface BulletProps {
  idx: number;
}

const Bullet = ({ idx }: BulletProps) => (
  <div
    className="w-1.5 h-1.5 rounded-full"
    style={{
      width: "6px",
      height: "6px",
      borderRadius: 9999,
      background: UserColorsMap[idx],
    }}
  />
);

interface PercentageLineProps {
  idx: number;
  percentage: number;
}

const PercentageLine = ({ idx, percentage }: PercentageLineProps) => {
  const baseColor = `${UserColorsMap[idx]}1A`; // 10% opacity
  const percentageColor = UserColorsMap[idx];

  return (
    <div
      style={{
        width: "100%",
        height: "6px",
        position: "relative",
        background: baseColor,
        borderRadius: 5,
      }}
    >
      <div
        style={{
          width: `${percentage}%`,
          height: "100%",
          position: "absolute",
          background: percentageColor,
          borderRadius: 5,
        }}
      />
    </div>
  );
};

interface ExecutionUsagePerUserProps {
  idx: number;
  name: string;
  percentage: number;
  executions: number;
}

const ExecutionUsagePerUser = ({
  idx,
  name,
  percentage,
  executions,
}: ExecutionUsagePerUserProps) => (
  <ShadcnTooltip title={`${executions} Credits (${percentage.toFixed(2)}%)`}>
    <div className={styles.user_execution_chart}>
      <div className={styles.user_execution_name}>
        <Bullet idx={idx} />
        <div className={styles.name}>{name}</div>
      </div>
      <PercentageLine idx={idx} percentage={percentage} />
    </div>
  </ShadcnTooltip>
);

interface UserExecutionData {
  user_id: number;
  display_name: string;
  percentage_executions: number;
  executions: number;
}

interface ExecutionUsagePerUsersProps {
  executionData: UserExecutionData[];
}

export const ExecutionUsagePerUsers = ({ executionData }: ExecutionUsagePerUsersProps) => (
  <div className={styles.user_execution_charts}>
    {executionData.map((user, idx) => (
      <ExecutionUsagePerUser
        key={idx}
        idx={idx}
        name={user.display_name}
        percentage={user.percentage_executions}
        executions={user.executions}
      />
    ))}
  </div>
);

type PieDataName = keyof typeof PieColorsMap;

export interface PieDataPoint {
  name: PieDataName;
  value: number;
}

interface CustomPieChartProps {
  data: PieDataPoint[];
  infoText: string;
  infoData: number | string;
  customStyle?: string;
  customPieStyle?: CSSProperties;
  showColor?: boolean;
}

export const CustomPieChart = ({
  data,
  infoText,
  infoData,
  customStyle = "",
  customPieStyle = undefined,
  showColor = true,
}: CustomPieChartProps) => (
  <div className={styles.pie} style={customPieStyle}>
    <ResponsiveContainer width="100%" height="100%">
      <PieChart>
        <Pie
          data={data}
          dataKey="value"
          innerRadius={60}
          outerRadius={80}
          fill={showColor ? "#8884d8" : "#EEEFF2"}
          label={false}
          paddingAngle={5}
          cornerRadius={20}
        >
          {data.map((d, index) => (
            <Cell key={`cell-${index}`} fill={PieColorsMap[d.name]} />
          ))}
        </Pie>
      </PieChart>
    </ResponsiveContainer>
    <div className={styles.remaining}>
      <div className={styles.text}>{infoText}</div>
      <div
        className={classNames(styles.number, {
          [styles.extra_credits]: customStyle === "extra",
        })}
      >
        {infoData}
      </div>
    </div>
  </div>
);

interface PeriodOption {
  dropdown: string;
}

interface PeriodSelectProps {
  id: string;
  currentPeriod: string;
  setCurrentPeriod: (period: string) => void;
  periodsMap: Record<string, PeriodOption>;
}

export const PeriodSelect = ({
  id,
  currentPeriod,
  setCurrentPeriod,
  periodsMap,
}: PeriodSelectProps) => (
  <div className={styles.dropdowns}>
    <NativeSelect
      id={id}
      options={Object.entries(periodsMap).map(([key, val]) => ({
        label: val.dropdown,
        value: key,
      }))}
      onChange={setCurrentPeriod}
      size={"sm"}
      value={currentPeriod.toString()}
      label={periodsMap[currentPeriod].dropdown}
    />
  </div>
);
